import {
  AllChannelsIcon,
  Button,
  CriticalIcon,
  EmptyState,
  Flex,
  Heading,
  Skeleton,
  Text,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useOrganizationId,
  useOrganizationRole,
} from '~publish/contexts/AccountContext'

import { ChannelPermissionsTable } from './components/ChannelPermissionsTable'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { SettingsRow } from '../shared/components/SettingsRow'
import { useMyChannelPermissions } from './hooks/useMyChannelPermissions'

import type { JSX } from 'react'

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  member: 'Member',
}

const SectionHeader = ({
  title,
  description,
}: {
  title: string
  description: string
}): JSX.Element => (
  <Flex direction="column" gap="2xs">
    <Heading as="h2" size="sm">
      {title}
    </Heading>
    <Text color="subtle">{description}</Text>
  </Flex>
)

/**
 * Read-only Settings page where the current user can see their org-level
 * role and their per-channel, per-product access levels. Editing permissions
 * lives in Team Members and Channels (admins only).
 */
const RoleAndPermissions = (): JSX.Element => {
  const { loading: accountLoading } = useAccount()
  const organizationId = useOrganizationId()
  const role = useOrganizationRole()
  const { channels, loading, error } = useMyChannelPermissions({
    organizationId,
  })

  if (accountLoading || loading) {
    return (
      <NewSettingsPageLayout title="Role & Permissions" gap="lg">
        <Skeleton width="100%" height="56px" />
        <Skeleton width="100%" height="220px" />
      </NewSettingsPageLayout>
    )
  }

  if (error) {
    return (
      <NewSettingsPageLayout title="Role & Permissions" gap="lg">
        <EmptyState variant="critical">
          <EmptyState.Icon>
            <CriticalIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>Failed to load permissions</EmptyState.Heading>
          <EmptyState.Description>
            Something went wrong. Please try again later.
          </EmptyState.Description>
        </EmptyState>
      </NewSettingsPageLayout>
    )
  }

  return (
    <NewSettingsPageLayout title="Role & Permissions" gap="lg">
      <SettingsRow id="role">
        <SettingsRow.Header>
          <SettingsRow.Title>Role</SettingsRow.Title>
          <SettingsRow.Description>
            Controls your access to settings, team, and billing.
          </SettingsRow.Description>
        </SettingsRow.Header>
        <SettingsRow.Control>
          {(ids) => (
            <Button
              variant="secondary"
              disabled
              id="role-value"
              aria-labelledby={`${ids.headingId} role-value`}
              aria-describedby={ids.descriptionId}
            >
              {(role && ROLE_LABELS[role]) ?? 'Member'}
            </Button>
          )}
        </SettingsRow.Control>
      </SettingsRow>

      <Flex direction="column" gap="md" fullWidth as="section">
        <SectionHeader
          title="Channel Permissions"
          description="Define what you can do in each channel, per product area."
        />
        {channels && channels.length > 0 ? (
          <ChannelPermissionsTable channels={channels} />
        ) : (
          <EmptyState size="medium">
            <EmptyState.Icon>
              <AllChannelsIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>No channel access</EmptyState.Heading>
            <EmptyState.Description>
              You don&apos;t have access to any channels in this organization.
              Reach out to an admin to request access.
            </EmptyState.Description>
          </EmptyState>
        )}
      </Flex>
    </NewSettingsPageLayout>
  )
}

export default RoleAndPermissions
